import React, { useState, useEffect } from "react";
import Layout from "../../components/layout";
import axios from "axios";
import { Col, Row, Typography, Avatar } from "antd";
import toast from "react-hot-toast";
import {
  CalendarOutlined, UserOutlined, CheckCircleOutlined,
  ClockCircleOutlined, FireOutlined, SyncOutlined
} from "@ant-design/icons";

const { Title, Text } = Typography;

/* ─── Tokens ─────────────────────────────────────────────────── */
const EMERALD    = "#0d9488";
const TEAL       = "#14b8a6";
const TEAL_LIGHT = "#ccfbf1";
const SLATE      = "#0f172a";
const SLATE_MID  = "#334155";
const MUTED      = "#94a3b8";
const SURFACE    = "#f8fafc";
const WHITE      = "#ffffff";

/* ─── Style injection ─────────────────────────────────────────── */
const injectStyles = () => {
  if (document.getElementById("slots-status-styles")) return;
  const el = document.createElement("style");
  el.id = "slots-status-styles";
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

    .ss-root { font-family: 'DM Sans', sans-serif; background: ${SURFACE}; min-height: 100vh; }
    .ss-wrap { max-width: 1040px; margin: 0 auto; padding: 32px 24px 64px; }

    /* Hero */
    .ss-hero {
      background: linear-gradient(135deg, ${SLATE} 0%, ${SLATE_MID} 100%);
      border-radius: 24px; padding: 36px 44px;
      position: relative; overflow: hidden; margin-bottom: 32px;
    }
    .ss-hero::before {
      content: ''; position: absolute; top: -80px; right: -80px;
      width: 300px; height: 300px; border-radius: 50%;
      background: radial-gradient(circle, ${TEAL}44 0%, transparent 70%);
      filter: blur(32px); pointer-events: none;
    }
    .ss-hero::after {
      content: ''; position: absolute; bottom: -60px; left: -40px;
      width: 200px; height: 200px; border-radius: 50%;
      background: radial-gradient(circle, ${EMERALD}33 0%, transparent 70%);
      filter: blur(26px); pointer-events: none;
    }
    .ss-eyebrow {
      display: inline-block; padding: 4px 12px; border-radius: 20px;
      background: ${TEAL_LIGHT}; color: ${EMERALD};
      font-size: 11px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase;
      font-family: 'Sora', sans-serif; margin-bottom: 12px;
    }
    .ss-hero-meta {
      display: flex; align-items: center; gap: 8px; margin-top: 14px;
    }
    .ss-hero-badge {
      background: rgba(255,255,255,0.12); border-radius: 20px;
      padding: 4px 12px; font-size: 12px; color: rgba(255,255,255,0.75);
      font-family: 'DM Sans', sans-serif;
    }

    /* Doctor card */
    .ss-card {
      background: ${WHITE}; border-radius: 20px;
      box-shadow: 0 4px 20px rgba(0,0,0,.06);
      padding: 24px; height: 100%;
      transition: transform .2s, box-shadow .2s;
      border: 1.5px solid #f1f5f9;
    }
    .ss-card:hover {
      transform: translateY(-3px);
      box-shadow: 0 12px 32px rgba(0,0,0,.1);
      border-color: ${TEAL}44;
    }

    /* Card header */
    .ss-card-head {
      display: flex; align-items: center; gap: 12px;
      margin-bottom: 20px; padding-bottom: 16px;
      border-bottom: 1.5px solid #f1f5f9;
    }

    /* Stat rows */
    .ss-stat {
      display: flex; align-items: center; justify-content: space-between;
      padding: 10px 14px; border-radius: 12px; margin-bottom: 8px;
      background: ${SURFACE};
    }
    .ss-stat-left {
      display: flex; align-items: center; gap: 8px;
    }
    .ss-stat-icon {
      width: 28px; height: 28px; border-radius: 8px;
      display: flex; align-items: center; justify-content: center;
      font-size: 13px; flex-shrink: 0;
    }

    /* Progress bar */
    .ss-progress-wrap {
      margin-top: 16px; padding: 14px;
      background: ${SURFACE}; border-radius: 12px;
    }
    .ss-progress-bar-bg {
      height: 8px; background: #e2e8f0; border-radius: 99px; overflow: hidden; margin-top: 8px;
    }
    .ss-progress-bar-fill {
      height: 100%; border-radius: 99px;
      background: linear-gradient(90deg, ${EMERALD}, ${TEAL});
      transition: width .6s ease;
    }

    /* Empty state */
    .ss-empty {
      background: ${WHITE}; border-radius: 20px;
      box-shadow: 0 4px 20px rgba(0,0,0,.06);
      padding: 60px 24px; text-align: center;
    }
    .ss-empty-icon {
      width: 64px; height: 64px; border-radius: 20px;
      background: ${TEAL_LIGHT}; color: ${EMERALD};
      display: flex; align-items: center; justify-content: center;
      font-size: 28px; margin: 0 auto 16px;
    }

    /* Animations */
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(16px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .ss-fade   { animation: fadeUp .5s ease both; }
    .ss-fade-1 { animation-delay: .05s; }

    /* stagger cards */
    .ss-card-wrap { animation: fadeUp .5s ease both; }
    .ss-card-wrap:nth-child(1) { animation-delay: .08s; }
    .ss-card-wrap:nth-child(2) { animation-delay: .14s; }
    .ss-card-wrap:nth-child(3) { animation-delay: .20s; }
    .ss-card-wrap:nth-child(4) { animation-delay: .26s; }
    .ss-card-wrap:nth-child(5) { animation-delay: .32s; }
    .ss-card-wrap:nth-child(6) { animation-delay: .38s; }
  `;
  document.head.appendChild(el);
};

/* ─── Stat row sub-component ──────────────────────────────────── */
function StatRow({ icon, iconBg, iconColor, label, value, valueColor }) {
  return (
    <div className="ss-stat">
      <div className="ss-stat-left">
        <div className="ss-stat-icon" style={{ background: iconBg, color: iconColor }}>
          {icon}
        </div>
        <Text style={{ fontSize: 12, color: SLATE_MID, fontWeight: 600 }}>{label}</Text>
      </div>
      <Text strong style={{ fontSize: 14, color: valueColor || SLATE, fontFamily: "'Sora', sans-serif" }}>
        {value}
      </Text>
    </div>
  );
}

/* ─── Main component ──────────────────────────────────────────── */
function DoctorSlotsStatus() {
  const [doctorsStatus, setDoctorsStatus] = useState([]);
  const [loading, setLoading]             = useState(true);

  useEffect(() => {
    injectStyles();
    const fetchDoctorsStatus = async () => {
      try {
        const response = await axios.get("/api/bookAppointment/doctor-slots-status", {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        if (response.data.success) {
          setDoctorsStatus(response.data.doctorsStatus);
        } else {
          toast.error(response.data.message);
        }
      } catch {
        toast.error("Something went wrong");
      } finally {
        setLoading(false);
      }
    };
    fetchDoctorsStatus();
  }, []);

  return (
    <Layout>
      <div className="ss-root">
        <div className="ss-wrap">

          {/* Hero */}
          <div className="ss-hero ss-fade">
            <div style={{ position: "relative", zIndex: 1 }}>
              <div className="ss-eyebrow">
                <SyncOutlined style={{ marginRight: 5 }} />Live Status
              </div>
              <Title level={2} style={{ margin: "6px 0 4px", fontFamily: "'Sora', sans-serif", color: WHITE, fontWeight: 800 }}>
                Doctor Slots Status
              </Title>
              <Text style={{ color: MUTED, fontSize: 14 }}>
                Real-time overview of appointment slot usage and reset schedules
              </Text>
              <div className="ss-hero-meta">
                <div className="ss-hero-badge">
                  <UserOutlined style={{ marginRight: 5 }} />{doctorsStatus.length} Doctor{doctorsStatus.length !== 1 ? "s" : ""}
                </div>
              </div>
            </div>
          </div>

          {/* Cards grid */}
          {!loading && doctorsStatus.length === 0 ? (
            <div className="ss-empty ss-fade ss-fade-1">
              <div className="ss-empty-icon"><CalendarOutlined /></div>
              <Title level={4} style={{ color: SLATE, fontFamily: "'Sora', sans-serif", marginBottom: 8 }}>
                No Data Available
              </Title>
              <Text style={{ color: MUTED, fontSize: 13 }}>
                No doctor slot information found. Check back after scheduling.
              </Text>
            </div>
          ) : (
            <Row gutter={[20, 20]}>
              {doctorsStatus.map((doctor) => {
                const used      = doctor.occupiedSlots ?? 0;
                const total     = doctor.totalSlots ?? 1;
                const available = doctor.availableSlots ?? 0;
                const pct       = Math.round((used / total) * 100);
                const isFull    = available === 0;

                return (
                  <Col xs={24} sm={12} lg={8} key={doctor._id} className="ss-card-wrap">
                    <div className="ss-card">

                      {/* Card header */}
                      <div className="ss-card-head">
                        <Avatar
                          size={44}
                          style={{
                            background: `linear-gradient(135deg, ${EMERALD}, ${TEAL})`,
                            fontFamily: "'Sora', sans-serif",
                            fontWeight: 700,
                            fontSize: 18,
                            flexShrink: 0,
                          }}
                        >
                          {(doctor.firstName?.[0] || "D").toUpperCase()}
                        </Avatar>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Text strong style={{ color: SLATE, fontSize: 14, fontFamily: "'Sora', sans-serif", display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            Dr. {doctor.firstName} {doctor.lastName || ""}
                          </Text>
                          <div style={{
                            display: "inline-block", marginTop: 3,
                            padding: "2px 10px", borderRadius: 20,
                            background: isFull ? "#fef2f2" : TEAL_LIGHT,
                            color: isFull ? "#ef4444" : EMERALD,
                            fontSize: 11, fontWeight: 700,
                          }}>
                            {isFull ? "Fully Booked" : "Available"}
                          </div>
                        </div>
                      </div>

                      {/* Stats */}
                      <StatRow
                        icon={<CalendarOutlined />}
                        iconBg="#eff6ff" iconColor="#3b82f6"
                        label="Total Slots" value={total}
                      />
                      <StatRow
                        icon={<CheckCircleOutlined />}
                        iconBg={TEAL_LIGHT} iconColor={EMERALD}
                        label="Available" value={available}
                        valueColor={available > 0 ? EMERALD : "#ef4444"}
                      />
                      <StatRow
                        icon={<FireOutlined />}
                        iconBg="#fef2f2" iconColor="#ef4444"
                        label="Used Slots" value={used}
                      />
                      <StatRow
                        icon={<ClockCircleOutlined />}
                        iconBg="#faf5ff" iconColor="#8b5cf6"
                        label="Days Until Reset"
                        value={doctor.daysUntilReset != null ? `${doctor.daysUntilReset}d` : "—"}
                      />

                      {/* Usage progress bar */}
                      <div className="ss-progress-wrap">
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Text style={{ fontSize: 11, fontWeight: 600, color: SLATE_MID }}>Slot Usage</Text>
                          <Text style={{ fontSize: 11, fontWeight: 700, color: pct > 80 ? "#ef4444" : EMERALD, fontFamily: "'Sora', sans-serif" }}>
                            {pct}%
                          </Text>
                        </div>
                        <div className="ss-progress-bar-bg">
                          <div
                            className="ss-progress-bar-fill"
                            style={{
                              width: `${pct}%`,
                              background: pct > 80
                                ? "linear-gradient(90deg, #f97316, #ef4444)"
                                : `linear-gradient(90deg, ${EMERALD}, ${TEAL})`,
                            }}
                          />
                        </div>
                      </div>

                    </div>
                  </Col>
                );
              })}
            </Row>
          )}

        </div>
      </div>
    </Layout>
  );
}

export default DoctorSlotsStatus;